import { z } from 'zod';
import { formatEnergy, formatPercent, formatPower } from './format.ts';

// Push notifications (Phase 6, ADR-0010). The gateway detects site events, STE Connect
// stores them, adds "gateway offline" itself and delivers them via FCM.

export const NotificationEvent = z.enum([
  'charge.finished',
  'vehicle.target_reached',
  'device.fault',
  'gateway.offline',
  'grid.dimmed',
  'network.wifi_weak',
]);
export type NotificationEvent = z.infer<typeof NotificationEvent>;

export const NOTIFICATION_EVENTS = NotificationEvent.options;

/** only for installers of the site (and STE staff), not for customers */
export const INSTALLER_EVENTS: readonly NotificationEvent[] = ['network.wifi_weak'];

const Param = z.union([z.string().max(200), z.number(), z.boolean()]);

export const SiteEvent = z.object({
  /** generated by the detecting side, used for idempotent delivery */
  id: z.uuid(),
  type: NotificationEvent,
  ts: z.iso.datetime({ offset: true }),
  params: z.record(z.string().max(40), Param).default({}),
});
export type SiteEvent = z.infer<typeof SiteEvent>;

export const DeviceEventsRequest = z.object({ events: z.array(SiteEvent).min(1).max(50) });

export const PushPlatform = z.enum(['ios', 'android', 'web']);
export type PushPlatform = z.infer<typeof PushPlatform>;

export const PushTokenRequest = z
  .object({
    token: z.string().min(16).max(4096),
    platform: PushPlatform,
  })
  // APNs device tokens are hex (iOS sends them to STE Connect as they come from Apple)
  .refine((r) => r.platform !== 'ios' || /^[0-9a-f]{32,200}$/i.test(r.token), {
    message: 'Ungültiges iOS-Gerätetoken',
    path: ['token'],
  });

export const NotificationSettings = z.object({
  siteId: z.string(),
  events: z.array(NotificationEvent),
});
export type NotificationSettings = z.infer<typeof NotificationSettings>;

export const UpdateNotificationSettings = z.object({ events: z.array(NotificationEvent).max(20) });

export const PushTestResponse = z.object({ sent: z.number().int(), failed: z.number().int() });
export type PushTestResponse = z.infer<typeof PushTestResponse>;

const str = (value: unknown) => (typeof value === 'string' && value.trim() ? value : undefined);
const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

/** German notification text, shared by the cloud (push) and the UI (event log) */
export function renderNotification(
  event: Pick<SiteEvent, 'type' | 'params'>,
  siteTitle?: string,
): { title: string; body: string } {
  const p = event.params;
  const where = siteTitle ? ` – ${siteTitle}` : '';
  switch (event.type) {
    case 'charge.finished': {
      const vehicle = str(p.vehicle) ?? str(p.loadpoint) ?? 'Fahrzeug';
      const energy = num(p.energyKWh);
      const soc = num(p.soc);
      const parts = [energy !== undefined ? `${formatEnergy(energy)} geladen` : 'Ladevorgang beendet'];
      if (soc !== undefined) parts.push(`Ladestand ${formatPercent(soc)}`);
      return { title: `${vehicle}: Laden beendet${where}`, body: parts.join(', ') };
    }
    case 'vehicle.target_reached': {
      const vehicle = str(p.vehicle) ?? str(p.loadpoint) ?? 'Fahrzeug';
      const soc = num(p.soc);
      const energy = num(p.energyKWh);
      const parts = [
        soc !== undefined ? `Ladestand ${formatPercent(soc)}` : 'Der gewünschte Ladestand ist erreicht',
      ];
      if (energy !== undefined) parts.push(`${formatEnergy(energy)} geladen`);
      return { title: `${vehicle}: Ziel erreicht${where}`, body: parts.join(', ') };
    }
    case 'device.fault':
      return {
        title: `Störung${where}`,
        body: str(p.message) ?? 'Ein Gerät meldet eine Störung. Details in der STE-App.',
      };
    case 'gateway.offline':
      return {
        title: `Anlage nicht erreichbar${where}`,
        body: 'Das STE-Gateway meldet sich nicht mehr. Bitte Internetverbindung und Strom prüfen.',
      };
    case 'network.wifi_weak': {
      const ssid = str(p.ssid);
      const signal = num(p.signal);
      return {
        title: `Schwaches WLAN-Signal${where}`,
        body: `Das Gerät ist nur schwach mit dem WLAN${ssid ? ` „${ssid}“` : ''} verbunden${
          signal !== undefined ? ` (Signal ${formatPercent(signal)})` : ''
        }. Ein Repeater oder ein Netzwerkkabel macht die Verbindung stabiler.`,
      };
    }
    case 'grid.dimmed': {
      const limit = num(p.maxPowerW);
      return {
        title: `Netzbetreiber dimmt (§14a)${where}`,
        body:
          limit !== undefined
            ? `Steuerbare Verbraucher sind vorübergehend auf ${formatPower(limit)} begrenzt.`
            : 'Steuerbare Verbraucher sind vorübergehend begrenzt.',
      };
    }
  }
}
